import React, { useState } from 'react';
import { X, Check } from 'lucide-react';

interface UsernamePromptProps {
  onSubmit: (username: string) => Promise<void>;
  onClose: () => void;
  error?: string;
}

export function UsernamePrompt({ onSubmit, onClose, error }: UsernamePromptProps) {
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;

    try {
      setIsSubmitting(true);
      await onSubmit(username.trim());
      setShowSuccess(true);
      // Auto-close after showing success message
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-black/90 rounded-lg p-6 w-full max-w-md mx-4 relative border border-white/10">
          <div className="flex flex-col items-center justify-center space-y-4 py-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-center">Account Created!</h3>
            <p className="text-gray-400 text-center">
              Welcome to SwordScan, {username}! Connecting your wallet...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-black/90 rounded-lg p-6 w-full max-w-md mx-4 relative border border-white/10">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-bold mb-4">Welcome to SwordScan!</h2>
        <p className="text-gray-400 mb-6">
          Please choose a username for your account. This will be displayed across the platform.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                placeholder="Choose a username"
                required
                minLength={3}
                maxLength={20}
                pattern="[a-zA-Z0-9_-]+"
                title="Username can only contain letters, numbers, underscores, and hyphens"
                disabled={isSubmitting}
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !username.trim()}
              className={`w-full py-2 px-4 rounded-lg font-medium ${
                isSubmitting || !username.trim()
                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {isSubmitting ? 'Setting username...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}